import { useEffect, useRef } from 'react';

/**
 * VisualizerView: React wrapper for the legacy CSP Visualizer.
 *
 * Renders the full HTML structure that the legacy App.js controller expects,
 * then instantiates it via useEffect after the DOM is ready.
 */
export default function VisualizerView() {
    const appRef = useRef(null);

    useEffect(() => {
        // Dynamically import legacy controller to avoid loading it at module scope
        let cancelled = false;

        import('../js/App.js').then(({ default: App }) => {
            if (cancelled) return;
            appRef.current = new App();
        });

        return () => {
            cancelled = true;
            if (appRef.current && typeof appRef.current.destroy === 'function') {
                appRef.current.destroy();
            }
            appRef.current = null;
        };
    }, []);

    return (
        <div className="container">
            <header>
                <nav
                    className="navbar"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto minmax(200px, 1fr) auto',
                        gap: '24px',
                        alignItems: 'center',
                    }}
                >
                    {/* Problem Type Buttons */}
                    <div className="nav-links" style={{ justifySelf: 'start' }}>
                        <button className="nav-btn" data-type="sudoku">Sudoku</button>
                        <button className="nav-btn active" data-type="map_coloring">Map Coloring</button>
                    </div>

                    {/* Solve Button */}
                    <button id="btn-solve" className="action-btn" style={{ justifySelf: 'end', whiteSpace: 'nowrap' }}>
                        ✨ Solve &amp; Visualize
                    </button>

                    {/* Sudoku Controls */}
                    <div
                        id="navbar-sudoku-controls"
                        style={{ display: 'none', gap: '12px', alignItems: 'center', justifySelf: 'end', whiteSpace: 'nowrap' }}
                    >
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'rgba(255,255,255,0.9)' }}>
                            Difficulty:
                            <select id="select-difficulty">
                                <option value="easy">Easy</option>
                                <option value="medium" defaultValue>Medium</option>
                                <option value="hard">Hard</option>
                                <option value="expert">Expert</option>
                                <option value="empty">Empty</option>
                            </select>
                        </label>
                        <label className="checkbox-label" style={{ fontSize: '13px' }}>
                            <input type="checkbox" id="check-random" /> Random Start
                        </label>
                    </div>
                </nav>

                {/* Playback Controls */}
                <div className="controls-right">
                    <div className="playback-group">
                        <button id="btn-prev" title="Previous Step">◀</button>
                        <button id="btn-play" className="primary" title="Play/Pause">▶</button>
                        <button id="btn-next" title="Next Step">▶</button>
                        <button id="btn-reset" title="Reset">↺</button>

                        <div className="speed-control">
                            <span>Speed</span>
                            <input type="range" id="input-speed" min="1" max="100" defaultValue="20" />
                        </div>
                    </div>

                    <div className="meta-group">
                        <span id="status-badge" className="status-badge hidden">Ready</span>
                        <span id="step-counter" className="step-badge">Step: 0 / 0</span>
                    </div>
                </div>
            </header>

            <div id="error-container"></div>

            <div className="layout">
                <div className="left-column">
                    {/* Graph Panel */}
                    <div id="graph-panel" className="panel">
                        <div className="panel-header">
                            <h2>Constraint Graph</h2>
                        </div>
                        <div className="graph-layout-container">
                            <div id="graph-legend-side" className="side-legend">
                                <div className="legend-item"><span className="swatch gray"></span> Unassigned</div>
                                <div className="legend-item"><span className="swatch green"></span> Assigned</div>
                                <div className="legend-item"><span className="swatch red"></span> Selected</div>
                                <div className="legend-item"><span className="swatch orange"></span> Active Check</div>
                            </div>
                            <div id="graph-container">
                                <div className="empty-state">
                                    <p>Click <strong>✨ Solve &amp; Visualize</strong> to start.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Event Log Panel */}
                    <div id="event-log-panel" className="panel">
                        <div className="panel-header">
                            <h2>Event Log</h2>
                            <div className="log-filters">
                                <input type="number" id="log-filter-start" placeholder="Start" min="0" />
                                <input type="number" id="log-filter-end" placeholder="End" min="0" />
                                <select id="log-filter-name">
                                    <option value="">All Events</option>
                                </select>
                            </div>
                        </div>
                        <div id="event-log">
                            <div id="event-log-content">
                                <div className="empty-state">Algorithm steps will be logged here</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="right-column">
                    {/* Variable State Panel */}
                    <div className="panel">
                        <div className="panel-header">
                            <h2>Variable State</h2>
                        </div>
                        <div className="variable-state-layout">
                            <div id="active-focus-pane" className="focus-pane side-focus hidden"></div>
                            <div id="state-container" className="table-container">
                                <div className="empty-state">No variable data</div>
                            </div>
                        </div>
                    </div>

                    {/* Current Event Panel */}
                    <div className="panel">
                        <h2>Current Event</h2>
                        <div id="event-details">
                            <div className="empty-state">
                                <p>Algorithm details and <strong>queue status</strong> will appear here during execution.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
